import React, { useState, useEffect } from "react";
import {
    Button,
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Table,
    Pagination,
    PaginationItem,
    PaginationLink,
} from "reactstrap";

// Use styling-capable XLSX library; falls back if unavailable
let XLSX;
try {
    XLSX = require('xlsx-js-style');
} catch (e) {
    XLSX = require('xlsx');
}

const NotificationReport = ({ onBack, notificationData, companyName, fromDate, toDate, notificationType, notificationStatus }) => {
    const [reportData, setReportData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        if (notificationData?.payload) {
            // Transform the notification data for table display
            // The data is organized by machine serial numbers
            const allNotifications = [];
            let srno = 1;

            Object.keys(notificationData.payload).forEach(serialNumber => {
                const notifications = notificationData.payload[serialNumber];
                notifications.forEach(item => {
                    allNotifications.push({
                        srno: srno++,
                        ...item
                    });
                });
            });

            setReportData(allNotifications);
            setCurrentPage(1); // Reset to first page when data changes
        }
    }, [notificationData]);

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = reportData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(reportData.length / itemsPerPage);

    // Change page
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleDownload = () => {
        if (!reportData || reportData.length === 0) {
            return;
        }

        // Prepare data for Excel - use ALL reportData, not just current page
        const excelData = reportData.map(item => ({
            'Sr. No.': item.srno,
            'Company Name': item.company_name || '',
            'Machine Name': item.machine_name || '',
            'Machine Number': item.serial_number || '',
            'Type': item.type || '',
            'Reason': item.reason || '',
            'Specified Value': item.specified_value || '',
            'Observed Value': item.observed_value || '',
            'Message': item.message || '',
            'Status': item.status || '',
            'Created By': item.created_by_name || '',
            'Resolved By': item.resolved_by_name || '',
            'Created On': item.created_at ? new Date(item.created_at).toLocaleString() : '',
            'Resolved On': item.resolved_at ? new Date(item.resolved_at).toLocaleString() : '',
        }));

        // Create worksheet with empty array first to add heading
        const ws = XLSX.utils.aoa_to_sheet([]);

        // Add heading in first row
        XLSX.utils.sheet_add_aoa(ws, [['Notifications Report']], { origin: 'A1' });

        // Add data starting from row 3 (leaving row 2 empty for spacing)
        XLSX.utils.sheet_add_json(ws, excelData, { origin: 'A3', skipHeader: false });

        // Merge cells for heading (covers all columns)
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 13 } }];

        // Style the heading cell (if xlsx-js-style is available)
        if (ws['A1']) {
            ws['A1'].s = {
                font: { bold: true, sz: 16 },
                alignment: { horizontal: 'center', vertical: 'center' },
                fill: { fgColor: { rgb: 'E8F4FD' } }
            };
        }

        // Set column widths
        ws['!cols'] = [
            { wch: 8 },  // Sr. No.
            { wch: 20 }, // Company Name
            { wch: 20 }, // Machine Name
            { wch: 15 }, // Serial Number
            { wch: 12 }, // Type
            { wch: 25 }, // Reason
            { wch: 25 }, // Specified Value
            { wch: 25 }, // Observed Value
            { wch: 50 }, // Message
            { wch: 12 }, // Status
            { wch: 20 }, // Created By
            { wch: 20 }, // Resolved By
            { wch: 20 }, // Created At
            { wch: 20 }, // Resolved At
        ];

        // Create workbook and add worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Notifications Report");

        // Generate filename
        const filename = `Notifications_Report_${companyName || 'Company'}_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Download file
        XLSX.writeFile(wb, filename);
    };

    return (
        <Container fluid className="mt-4">
            <Row>
                <Col xl="11" className="mx-auto">
                    <Card className="shadow border-0">
                        <CardBody className="bg-gray-50">
                            {/* Stats Section */}
                            <Row className="mb-3">
                                <Col lg="9">
                                    <div className="text-sm text-gray-500">
                                        <Button
                                            color="link"
                                            onClick={onBack}
                                            className="p-0 text-xs no-underline"
                                        >
                                            <i className="fa fa-arrow-left mr-2"></i>
                                            Back
                                        </Button>
                                        <i className="fa fa-bell mr-1"></i>
                                        Notifications Report - {reportData.length} records
                                        {companyName && <span className="ml-2">| {companyName}</span>}
                                    </div>
                                </Col>
                                <Col lg="3" className="text-right">
                                    <div
                                        onClick={handleDownload}
                                        style={{
                                            padding: '2px 4px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            cursor: 'pointer',
                                            color: '#418efc',
                                            borderBottom: '1.5px solid #418efc',
                                        }}
                                    >
                                        <i className="fa fa-download mr-1"></i>
                                        Download Excel
                                    </div>
                                </Col>
                                <Col>
                                    <div style={{
                                        display: 'inline', marginBottom: '10px', fontSize: '16px', textDecoration: 'underline',
                                    }}>
                                        <i className="fa fa-bell mr-1"></i>
                                        Notifications Report - {reportData.length} records
                                        {companyName && <span>| {companyName}</span>}
                                        {fromDate && <span>| From: {fromDate}</span>}
                                        {toDate && <span>| To: {toDate}</span>}
                                        {notificationType && notificationType.length > 0 && <span>| Type: {Array.isArray(notificationType) ? notificationType.join(", ") : notificationType}</span>}
                                        {notificationStatus && notificationStatus.length > 0 && <span>| Status: {Array.isArray(notificationStatus) ? notificationStatus.join(", ") : notificationStatus}</span>}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    {reportData && reportData.length > 0 ? (
                                        <>
                                            <div style={{ overflowX: 'auto' }}>
                                                <Table bordered responsive className="align-items-center table-flush">
                                                    <thead className="thead-light">
                                                        <tr>
                                                            <th style={{ minWidth: '60px' }}>Sr. No.</th>
                                                            <th style={{ minWidth: '150px' }}>Company</th>
                                                            <th style={{ minWidth: '150px' }}>Machine Name</th>
                                                            <th style={{ minWidth: '120px' }}>Machine Number</th>
                                                            <th style={{ minWidth: '100px' }}>Type</th>
                                                            <th style={{ minWidth: '200px' }}>Reason</th>
                                                            <th style={{ minWidth: '150px' }}>Specified Value</th>
                                                            <th style={{ minWidth: '150px' }}>Observed Value</th>
                                                            <th style={{ minWidth: '300px' }}>Message</th>
                                                            <th style={{ minWidth: '120px' }}>Status</th>
                                                            <th style={{ minWidth: '150px' }}>Created By</th>
                                                            <th style={{ minWidth: '150px' }}>Resolved By</th>
                                                            <th style={{ minWidth: '180px' }}>Created On</th>
                                                            <th style={{ minWidth: '180px' }}>Resolved On</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentItems.map((item) => (
                                                            <tr key={item.srno}>
                                                                <td>{item.srno}</td>
                                                                <td>{item.company_name || '-'}</td>
                                                                <td>{item.machine_name || '-'}</td>
                                                                <td>{item.serial_number || '-'}</td>
                                                                <td>
                                                                    <span className={`badge badge-${item.type === 'danger' ? 'danger' :
                                                                        item.type === 'alert' ? 'info' :
                                                                            item.type === 'warning' ? 'warning' :
                                                                                'info'
                                                                        }`} style={{ textTransform: 'uppercase' }}>
                                                                        {item.type || 'info'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                                    {item.reason || '-'}
                                                                </td>
                                                                <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                                    {item.specified_value || '-'}
                                                                </td>
                                                                <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                                    {item.observed_value || '-'}
                                                                </td>
                                                                <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                                    {item.message || '-'}
                                                                </td>
                                                                <td>
                                                                    <span className={`badge badge-${item.status === 'resolved' ? 'success' :
                                                                        item.status === 'unresolved' ? 'warning' :
                                                                            item.status === 'pending' ? 'warning' :
                                                                                'secondary'
                                                                        }`} style={{ textTransform: 'capitalize', minWidth: '90px', display: 'inline-block' }}>
                                                                        {item.status || 'pending'}
                                                                    </span>
                                                                </td>
                                                                <td>{item.created_by_name || '-'}</td>
                                                                <td>{item.resolved_by_name || '-'}</td>
                                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                                    {item.created_at ? new Date(item.created_at).toLocaleString() : '-'}
                                                                </td>
                                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                                    {item.resolved_at ? new Date(item.resolved_at).toLocaleString() : '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Pagination - only show if more than 10 records */}
                                            {reportData.length > 10 && (
                                                <Row className="mt-3">
                                                    <Col className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, reportData.length)} of {reportData.length} entries
                                                        </div>
                                                        <Pagination>
                                                            <PaginationItem disabled={currentPage === 1}>
                                                                <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                                                            </PaginationItem>
                                                            {[...Array(totalPages)].map((_, index) => {
                                                                const pageNumber = index + 1;
                                                                // Show first page, last page, current page, and pages around current
                                                                if (
                                                                    pageNumber === 1 ||
                                                                    pageNumber === totalPages ||
                                                                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                                                ) {
                                                                    return (
                                                                        <PaginationItem key={pageNumber} active={pageNumber === currentPage}>
                                                                            <PaginationLink onClick={() => handlePageChange(pageNumber)}>
                                                                                {pageNumber}
                                                                            </PaginationLink>
                                                                        </PaginationItem>
                                                                    );
                                                                } else if (
                                                                    pageNumber === currentPage - 2 ||
                                                                    pageNumber === currentPage + 2
                                                                ) {
                                                                    return <PaginationItem key={pageNumber} disabled><PaginationLink>...</PaginationLink></PaginationItem>;
                                                                }
                                                                return null;
                                                            })}
                                                            <PaginationItem disabled={currentPage === totalPages}>
                                                                <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                                                            </PaginationItem>
                                                        </Pagination>
                                                    </Col>
                                                </Row>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-center text-muted py-5">
                                            <h4>No notification data available</h4>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row >
        </Container >
    );
};

export default NotificationReport;